import React, { useMemo, useState, useEffect } from 'react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { FormikProvider, useFormik } from 'formik';
import { Typography, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';

import countries from '~constants/countries';
import useBoolean from 'common/hooks/useBoolean';
import { phoneNumberJoin } from '~helpers/order';
import event from '~helpers/mixpanel';
import { authActionTypes } from '~client/constants/actionTypes';
import { MUTATION_LANDLORD_ACCOUNT_SIGNUP, MUTATION_GENERATE_CAPTCHA } from './queries';
import { validateTelephone, validateInternationalTelephone } from '~helpers/validate';
import {
  FieldSelectPhoneNumber,
  FieldInput,
  getPhoneNumberModalErrorTip,
} from './login-formik-fields';
import { loginContentTypes } from './login-content';
import CaptchaModal, {
  CAPTCHA_SIZE,
  VALIDATION_WIDTH,
  VALIDATION_HEIGHT,
  getCaptchaModalErrorTip,
} from './captcha-modal';

const useStyles = makeStyles(
  () => ({
    formik: {
      paddingTop: 20,

      '& > *:not(:first-child)': {
        width: '100%',
        marginTop: 16,
      },
      '& > :last-child': {
        marginTop: 20,
      },
    },
    helperText: {
      fontSize: '12px',
      color: '#595959',
      marginTop: '8px !important',
      marginBottom: '0 !important',
    },
  }),
  { name: 'LoginOrSignUp' },
);

const FinishSignupForEmailOrPhone = ({ signupValue, loginContentType, backPhoneNumberLogin }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();

  const [captchaErrorTip, setCaptchaErrorTip] = useState('');
  const [phoneNumberModalErrorTip, setPhoneNumberModalErrorTip] = useState('');
  const [existsTip, setExistsTip] = useState('');
  const [open, { setTrue: openModal, setFalse: closeModal }] = useBoolean();

  const authMethod =
    loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP
      ? 'Email Signup'
      : 'Phone Signup';

  const [generateCaptcha, { data: captchaValidation, loading: generateCaptchaLoading }] =
    useMutation(MUTATION_GENERATE_CAPTCHA, {
      variables: {
        captchaSize: CAPTCHA_SIZE,
        validationWidth: VALIDATION_WIDTH,
        validationHeight: VALIDATION_HEIGHT,
      },
      onError: () => {
        message.error(t('sorry_an_error_occurred_please_try_again'));
      },
    });

  const [mutationLandlordAccountSignup, { loading: landlordAccountSignupLoading }] = useMutation(
    MUTATION_LANDLORD_ACCOUNT_SIGNUP,
    {
      onCompleted: successData => {
        const authToken = successData?.landlordAccountSignUp?.authToken;
        dispatch({ type: authActionTypes.LOGGED_IN, token: authToken });
        event('Successfully Signed Up');
      },
      onError: e => {
        const errMassageText = e?.toString() ?? '';
        const captchaErrorTip = getCaptchaModalErrorTip(errMassageText);

        if (captchaErrorTip && loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP) {
          setCaptchaErrorTip(t(captchaErrorTip));
          generateCaptcha();
          return;
        }

        if (
          captchaErrorTip &&
          loginContentType === loginContentTypes.FINISH_PHONE_NUMBER_FOR_SIGNUP
        ) {
          backPhoneNumberLogin();
          return;
        }

        formik?.setSubmitting(false);
        closeModal();
        setCaptchaErrorTip();

        const PhoneNumberModalErrorTip = getPhoneNumberModalErrorTip(errMassageText);
        if (PhoneNumberModalErrorTip) {
          setPhoneNumberModalErrorTip(t(PhoneNumberModalErrorTip));
          return;
        }

        setPhoneNumberModalErrorTip();

        if (errMassageText.includes('LANDLORD_ALREADY_EXIST')) {
          setExistsTip(t('the_user_already_exists'));
          return;
        }

        setExistsTip();

        if (errMassageText) {
          message.error(e.toString());
          return;
        }

        message.error(t('sorry_an_error_occurred_please_try_again'));
      },
    },
  );

  const validationSchema = useMemo(() => {
    const yups = {
      firstName: yup.string().max(200).required(t('this_part_cannot_be_empty')),
      lastName: yup.string().max(200).required(t('this_part_cannot_be_empty')),
    };

    if (loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP) {
      yups.phone = yup
        .number()
        .test('valid_number', t('please_enter_a_valid_phone_number'), value => {
          if (value) {
            return validateTelephone(value) && validateInternationalTelephone(value);
          }
          return true;
        })
        .required(t('this_part_cannot_be_empty'));
      yups.phoneCode = yup.string().required(t('this_part_cannot_be_empty'));
    } else {
      yups.email = yup
        .string()
        .label('Email')
        .email(t('landlord.auth.login.format_error.username'))
        .max(200)
        .required(t('this_part_cannot_be_empty'));
    }

    return yup.object().shape({ ...yups });
  }, [loginContentType, t]);

  const createAccountByPhoneNumber = async formikValues => {
    const variablesInput = {
      newsletterAccepted: !!signupValue?.newsletterAccepted,
      phone: signupValue?.loginPhone,
      phoneCode: signupValue?.phoneCodeForInput,
      email: formikValues?.email,
      firstName: formikValues?.firstName,
      lastName: formikValues?.lastName,
    };

    await mutationLandlordAccountSignup({ variables: { input: variablesInput } });
  };

  const handleCreateAccount = values => {
    event('Clicked Finishing Signup Page', {
      'Clicked Position': 'Finish Btn',
      'Auth Method': authMethod,
    });

    if (loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP) {
      openModal();
      generateCaptcha();
    } else {
      createAccountByPhoneNumber(values);
    }
  };

  const phoneCode = useMemo(() => {
    const countryCode = window.hurricane.clientInfo?.country?.toLowerCase();

    return countries.list.find(item => item.iso2Code === countryCode)?.phoneCode || '+86';
  }, []);

  const initialValues = useMemo(
    () => ({ firstName: '', lastName: '', phoneCode: phoneCode, phone: '', email: '' }),
    [phoneCode],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleCreateAccount,
  });

  const closeCaptchaModalAndCreateAccount = async (captchaCode, captchaId) => {
    if (captchaCode && captchaId) {
      const variablesInput = {
        email: signupValue?.email,
        newsletterAccepted: !!signupValue?.newsletterAccepted,
        password: signupValue?.password,
        captchaCode,
        captchaId,
        firstName: formik?.values?.firstName,
        lastName: formik?.values?.lastName,
        phone: phoneNumberJoin({
          phoneCode: formik?.values?.phoneCode,
          phoneNumber: formik?.values?.phone,
        }),
      };

      await mutationLandlordAccountSignup({ variables: { input: variablesInput } });
    }
  };

  const handleTrackPosition = clickedPosition => {
    event('Clicked Finishing Signup Page', {
      'Clicked Position': clickedPosition,
      'Auth Method': authMethod,
    });
  };

  useEffect(() => {
    if (phoneNumberModalErrorTip && !formik?.errors?.phone) {
      formik?.setErrors({ phone: phoneNumberModalErrorTip });
      setPhoneNumberModalErrorTip();
    }

    const fieldType =
      loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP ? 'phone' : 'email';

    if (!phoneNumberModalErrorTip && !formik?.errors?.[fieldType] && existsTip) {
      formik?.setErrors({
        [fieldType]: existsTip,
      });
      setExistsTip();
    }
  }, [existsTip, formik, loginContentType, phoneNumberModalErrorTip]);

  useEffect(() => {
    event('Viewed Finishing Signup Page', {
      'Auth Method': authMethod,
    });
  }, [authMethod, loginContentType]);

  return (
    <div className={classes.formik}>
      <FormikProvider value={formik}>
        <FieldInput
          fieldConfig="firstName"
          placeholder={t('first_name')}
          onFocus={() => handleTrackPosition('First Name')}
        />
        <FieldInput
          fieldConfig="lastName"
          placeholder={t('last_name')}
          onFocus={() => handleTrackPosition('Last Name')}
        />
        <Typography className={classes.helperText}>
          {t('tell_us_how_we_should_address_you')}
        </Typography>
        {loginContentType === loginContentTypes.FINISH_EMAIL_FOR_SIGNUP && (
          <FieldSelectPhoneNumber
            fieldConfig="phone"
            phoneCodeFieldConfig={'phoneCode'}
            placeholder={t('phone_number')}
            style={{ marginTop: '16px !important' }}
            onSelectFocus={() => handleTrackPosition('Country Code')}
            onFocus={() => handleTrackPosition('Phone Box')}
          />
        )}

        {loginContentType === loginContentTypes.FINISH_PHONE_NUMBER_FOR_SIGNUP && (
          <FieldInput
            fieldConfig="email"
            placeholder={t('email_address')}
            onFocus={() => handleTrackPosition('Email Box')}
          />
        )}

        <Typography className={classes.helperText}>
          {t('this_is_for_your_account_security_and_for_our_staff_to_reah_out_and_offer_help')}
        </Typography>
        <Button
          type="primary"
          size="large"
          loading={landlordAccountSignupLoading}
          onClick={formik.handleSubmit}
        >
          {t('finish')}
        </Button>
      </FormikProvider>

      {open && (
        <CaptchaModal
          authMethod={authMethod}
          captchaErrorTip={captchaErrorTip}
          captchaValidation={captchaValidation}
          closeCaptchaModalAndCreateAccount={closeCaptchaModalAndCreateAccount}
          generateCaptcha={generateCaptcha}
          generateCaptchaLoading={generateCaptchaLoading}
          onCloseCaptchaModal={closeModal}
          openCaptchaModal={open}
          setCaptchaErrorTip={setCaptchaErrorTip}
          isLoading={landlordAccountSignupLoading}
        />
      )}
    </div>
  );
};

FinishSignupForEmailOrPhone.propTypes = {
  signupValue: PropTypes.object,
  loginContentType: PropTypes.string,
  backPhoneNumberLogin: PropTypes.func.isRequired,
};

export default FinishSignupForEmailOrPhone;
